/**
 * TranslateModal — create a translated copy of the current CV.
 */

import { useState } from 'react';
import { Globe, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Modal } from '../../ui/Modal';

interface TranslateModalProps {
  open: boolean;
  onClose: () => void;
  activeTemplate: string;
  isTranslatingVersionId: string | null;
  onTranslate: (sourceId: string, targetLang: 'fr' | 'en') => Promise<void>;
}

export function TranslateModal({
  open,
  onClose,
  activeTemplate,
  isTranslatingVersionId,
  onTranslate,
}: TranslateModalProps) {
  const [targetLang, setTargetLang] = useState<'fr' | 'en'>('en');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isRunning = isTranslatingVersionId !== null;

  const handleTranslate = async () => {
    setError(null);
    try {
      await onTranslate(activeTemplate, targetLang);
      setDone(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erreur lors de la traduction';
      setError(msg);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { setDone(false); setError(null); onClose(); }}
      title="Traduire le CV"
      size="md"
    >
      <div className="space-y-5">
        <p className="text-sm text-white/60">
          L'IA crée une copie traduite de ton CV actuel dans la langue choisie.
          L'original n'est pas modifié.
        </p>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
            <AlertCircle className="size-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Erreur de traduction</p>
              <p className="mt-0.5 text-red-300/80">{error}</p>
              <button type="button" onClick={() => setError(null)} className="mt-1.5 text-xs underline opacity-70 hover:opacity-100">
                Réessayer
              </button>
            </div>
          </div>
        )}

        {done ? (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
            <CheckCircle2 className="size-5 shrink-0" />
            <p className="text-sm font-semibold">
              Nouvelle version traduite créée ! Elle est maintenant disponible dans tes versions.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <p className="text-xs font-semibold text-white/50 uppercase tracking-wider">Langue cible</p>
              <div className="grid grid-cols-2 gap-3">
                {(['en', 'fr'] as const).map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => setTargetLang(lang)}
                    className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-bold transition-all ${
                      targetLang === lang
                        ? 'bg-cyan-600/20 border-cyan-500/40 text-cyan-300 ring-2 ring-white/10'
                        : 'border-white/10 bg-white/[0.02] text-white/60 hover:border-white/20 hover:text-white/80'
                    }`}
                  >
                    <Globe className="size-4" />
                    {lang === 'en' ? '🇬🇧 Anglais' : '🇫🇷 Français'}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={handleTranslate}
              disabled={isRunning}
              className="flex items-center gap-2 w-full justify-center px-5 py-3 rounded-xl text-sm font-bold bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-600/20"
            >
              {isRunning ? (
                <><Loader2 className="size-4 animate-spin" />Traduction en cours…</>
              ) : (
                <><Globe className="size-4" />Créer la version traduite</>
              )}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}
